import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { EmployeeUserType } from "../../../types/employeeType";
import { GeneralButton } from "../../../common/ui/GeneralButton";
import { timeTrackingApi } from "../../../api/timeTrackingApi";
import { CreateTimeQuotaRequest, TimeQuota } from "../../../types/timeTracking";
import { HolidayExtent } from "../../../types/holidayExtent";
import { RegionalHolidays, RegionalHolidaysState } from "../Absence/RegionalHolidays";

type WorkdaysState = {
  monday: number;
  tuesday: number;
  wednesday: number;
  thursday: number;
  friday: number;
  saturday: number;
  sunday: number;
};

type WorkTimeQuotaDialogProps = {
  open: boolean;
  onClose: () => void;
  employee: EmployeeUserType;
  timeTrackingServiceModuleId?: number;
  canEdit: boolean;
  companyId: number | null;
};

const defaultWorkdays: WorkdaysState = {
  monday: 8,
  tuesday: 8,
  wednesday: 8,
  thursday: 8,
  friday: 8,
  saturday: 0,
  sunday: 0,
};

const defaultRegionalHolidays: RegionalHolidaysState = {
  holy3KingsDay: HolidayExtent.None,
  internationalWomensHoliday: HolidayExtent.None,
  fronleichnamHoliday: HolidayExtent.None,
  mariaHimmelfahrtHoliday: HolidayExtent.None,
  worldChildrenDayHoliday: HolidayExtent.None,
  reformationDayHoliday: HolidayExtent.None,
  allerheiligenDayHoliday: HolidayExtent.None,
  bussUndBetTagHoliday: HolidayExtent.None,
  augsburgerFriedensfestHoliday: HolidayExtent.None,
  christmasEveHoliday: HolidayExtent.None,
  newYearsEveHoliday: HolidayExtent.None,
  easterSundayHoliday: HolidayExtent.None,
  pfingstSundayHoliday: HolidayExtent.None,
};

const getIsoWeek = (date: Date) => {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNumber = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - dayNumber);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  return Math.ceil((((target.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

const getIsoWeekStart = (week: number, year: number) => {
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
  const dayOfWeek = simple.getUTCDay();
  const isoWeekStart = new Date(simple);
  if (dayOfWeek <= 4 && dayOfWeek > 0) {
    isoWeekStart.setUTCDate(simple.getUTCDate() - dayOfWeek + 1);
  } else {
    isoWeekStart.setUTCDate(simple.getUTCDate() + 8 - dayOfWeek);
  }
  return isoWeekStart;
};

const getIsoWeekCount = (year: number) => {
  const endOfYear = new Date(Date.UTC(year, 11, 28));
  return getIsoWeek(endOfYear);
};

const WorkTimeQuotaDialog = ({
  open,
  onClose,
  employee,
  timeTrackingServiceModuleId,
  canEdit,
  companyId,
}: WorkTimeQuotaDialogProps) => {
  const { t } = useTranslation();
  const [initialSurplusHours, setInitialSurplusHours] = useState("");
  const [pausesAreWorkingTime, setPausesAreWorkingTime] = useState(false);
  const [editPausesAreWorkingTime, setEditPausesAreWorkingTime] = useState(false);
  const [createStartWeek, setCreateStartWeek] = useState<number | "">("");
  const [createStartYear, setCreateStartYear] = useState<number>(dayjs().year());
  const [createEndWeek, setCreateEndWeek] = useState<number | "">("");
  const [createEndYear, setCreateEndYear] = useState<number>(dayjs().year());
  const [workdays, setWorkdays] = useState<WorkdaysState>(defaultWorkdays);
  const [editWorkdays, setEditWorkdays] = useState<WorkdaysState>(defaultWorkdays);
  const [regionalHolidays, setRegionalHolidays] = useState<RegionalHolidaysState>(
    defaultRegionalHolidays
  );
  const [editRegionalHolidays, setEditRegionalHolidays] = useState<RegionalHolidaysState>(
    defaultRegionalHolidays
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasExistingQuotas, setHasExistingQuotas] = useState<boolean | null>(null);
  const [existingQuotas, setExistingQuotas] = useState<TimeQuota[]>([]);
  const [isCreateExpanded, setIsCreateExpanded] = useState(true);
  const [isEditExpanded, setIsEditExpanded] = useState(false);
  const [editStartQuotaId, setEditStartQuotaId] = useState<number | "">("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setInitialSurplusHours("");
    setPausesAreWorkingTime(false);
    setEditPausesAreWorkingTime(false);
    setCreateStartWeek("");
    setCreateStartYear(dayjs().year());
    setCreateEndWeek("");
    setCreateEndYear(dayjs().year());
    setWorkdays(defaultWorkdays);
    setEditWorkdays(defaultWorkdays);
    setRegionalHolidays(defaultRegionalHolidays);
    setEditRegionalHolidays(defaultRegionalHolidays);
    setHasExistingQuotas(null);
    setExistingQuotas([]);
    setIsCreateExpanded(false);
    setIsEditExpanded(false);
    setEditStartQuotaId("");
    setErrorMessage(null);
    setSuccessMessage(null);
  }, [open, employee.id]);

  useEffect(() => {
    if (!open || !timeTrackingServiceModuleId) return;
    const fetchQuotas = async () => {
      const result = await timeTrackingApi.getTimeQuotas(
        timeTrackingServiceModuleId,
        employee.id
      );
      if (result.success() && Array.isArray(result.result)) {
        setExistingQuotas(result.result);
        setHasExistingQuotas(result.result.length > 0);
        setIsCreateExpanded(false);
        setIsEditExpanded(false);
      } else {
        setHasExistingQuotas(null);
        setExistingQuotas([]);
        setIsCreateExpanded(false);
        setIsEditExpanded(false);
      }
    };
    fetchQuotas();
  }, [open, timeTrackingServiceModuleId, employee.id]);

  const sortedQuotas = useMemo(() => {
    const currentYear = dayjs().year();
    return existingQuotas
      .filter((quota) => quota.year === currentYear)
      .sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.calendarWeek - b.calendarWeek;
      });
  }, [existingQuotas]);
  const sortedAllQuotas = useMemo(() => {
    return [...existingQuotas].sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.calendarWeek - b.calendarWeek;
    });
  }, [existingQuotas]);

  const firstQuota = sortedQuotas[0];
  const lastQuota = sortedQuotas[sortedQuotas.length - 1];
  const toHours = (minutes: number) => Math.round((minutes / 60) * 100) / 100;
  const currentYear = dayjs().year();

  const startWeekCount = useMemo(
    () => getIsoWeekCount(createStartYear),
    [createStartYear]
  );
  const endWeekCount = useMemo(
    () => getIsoWeekCount(createEndYear),
    [createEndYear]
  );
  const startWeekOptions = useMemo(
    () => Array.from({ length: startWeekCount }, (_, index) => index + 1),
    [startWeekCount]
  );
  const endWeekOptions = useMemo(
    () => Array.from({ length: endWeekCount }, (_, index) => index + 1),
    [endWeekCount]
  );

  useEffect(() => {
    if (!open || sortedAllQuotas.length === 0) {
      setEditStartQuotaId("");
      return;
    }
    setEditStartQuotaId((prev) => {
      if (prev === "" || !sortedAllQuotas.some((quota) => quota.quotaId === prev)) {
        return sortedAllQuotas[0].quotaId;
      }
      return prev;
    });
  }, [open, sortedAllQuotas]);

  useEffect(() => {
    if (!open) return;
    const defaultStartWeek = getIsoWeek(new Date());
    setCreateStartYear(currentYear);
    setCreateStartWeek(defaultStartWeek);
    setCreateEndYear(currentYear);
    setCreateEndWeek(getIsoWeekCount(currentYear));
  }, [open, currentYear]);

  const parseHolidayExtent = (value: any): HolidayExtent => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      const stringToEnum: Record<string, HolidayExtent> = {
        "None": HolidayExtent.None,
        "Half": HolidayExtent.Half,
        "Full": HolidayExtent.Full,
      };
      return stringToEnum[value] ?? HolidayExtent.None;
    }
    return HolidayExtent.None;
  };

  useEffect(() => {
    if (editStartQuotaId === "") return;
    const selectedQuota = sortedAllQuotas.find(
      (quota) => quota.quotaId === editStartQuotaId
    );
    if (!selectedQuota) return;
    setEditWorkdays({
      monday: selectedQuota.minutesMonday / 60,
      tuesday: selectedQuota.minutesTuesday / 60,
      wednesday: selectedQuota.minutesWednesday / 60,
      thursday: selectedQuota.minutesThursday / 60,
      friday: selectedQuota.minutesFriday / 60,
      saturday: selectedQuota.minutesSaturday / 60,
      sunday: selectedQuota.minutesSunday / 60,
    });
    setEditRegionalHolidays({
      holy3KingsDay: parseHolidayExtent(selectedQuota.holy3KingsDayHoliday),
      internationalWomensHoliday: parseHolidayExtent(selectedQuota.internationalWomensHoliday),
      fronleichnamHoliday: parseHolidayExtent(selectedQuota.fronleichnamHoliday),
      mariaHimmelfahrtHoliday: parseHolidayExtent(selectedQuota.mariaHimmelfahrtHoliday),
      worldChildrenDayHoliday: parseHolidayExtent(selectedQuota.worldChildrenDayHoliday),
      reformationDayHoliday: parseHolidayExtent(selectedQuota.reformationDayHoliday),
      allerheiligenDayHoliday: parseHolidayExtent(selectedQuota.allerheiligenDayHoliday),
      bussUndBetTagHoliday: parseHolidayExtent(selectedQuota.bussUndBetTagHoliday),
      augsburgerFriedensfestHoliday: parseHolidayExtent(selectedQuota.augsburgerFriedensfestHoliday),
      christmasEveHoliday: parseHolidayExtent(selectedQuota.christmasEveHoliday),
      newYearsEveHoliday: parseHolidayExtent(selectedQuota.newYearsEveHoliday),
      easterSundayHoliday: parseHolidayExtent(selectedQuota.easterSundayHoliday),
      pfingstSundayHoliday: parseHolidayExtent(selectedQuota.pfingstSundayHoliday),
    });
    setEditPausesAreWorkingTime(selectedQuota.pausesAreWorkingTime ?? false);
  }, [editStartQuotaId, sortedAllQuotas, isEditExpanded]);

  const handleSubmit = useCallback(async () => {
    setErrorMessage(null);
    setSuccessMessage(null);

    if (!canEdit) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.noPermission"));
      return;
    }

    if (!timeTrackingServiceModuleId) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.missingModule"));
      return;
    }

    if (!companyId) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.missingCompany"));
      return;
    }

    if (
      createStartWeek === ""
      || createEndWeek === ""
      || createEndYear < createStartYear
      || (createEndYear === createStartYear && createEndWeek < createStartWeek)
    ) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.invalidDateRange"));
      return;
    }

    let surplusMinutes = 0;
    if (hasExistingQuotas === false) {
      const surplusHours = Number(initialSurplusHours);
      if (initialSurplusHours === "" || Number.isNaN(surplusHours)) {
        setErrorMessage(t("employeesPage.workTimeQuota.errors.invalidSurplusHours"));
        return;
      }
      surplusMinutes = Math.round(surplusHours * 60);
    }

    const startDate = getIsoWeekStart(createStartWeek, createStartYear);
    const endDate = getIsoWeekStart(createEndWeek, createEndYear);
    endDate.setUTCDate(endDate.getUTCDate() + 6);
    const dateFrom = dayjs(startDate).format("YYYY-MM-DD");
    const dateUntil = dayjs(endDate).format("YYYY-MM-DD");

    const payload: CreateTimeQuotaRequest = {
      employeeUserId: employee.id,
      companyId,
      minutesMonday: workdays.monday * 60,
      minutesTuesday: workdays.tuesday * 60,
      minutesWednesday: workdays.wednesday * 60,
      minutesThursday: workdays.thursday * 60,
      minutesFriday: workdays.friday * 60,
      minutesSaturday: workdays.saturday * 60,
      minutesSunday: workdays.sunday * 60,
      createFrom: dateFrom,
      createUntil: dateUntil,
      holy3KingsDayHoliday: regionalHolidays.holy3KingsDay,
      internationalWomensHoliday: regionalHolidays.internationalWomensHoliday,
      fronleichnamHoliday: regionalHolidays.fronleichnamHoliday,
      mariaHimmelfahrtHoliday: regionalHolidays.mariaHimmelfahrtHoliday,
      worldChildrenDayHoliday: regionalHolidays.worldChildrenDayHoliday,
      reformationDayHoliday: regionalHolidays.reformationDayHoliday,
      allerheiligenDayHoliday: regionalHolidays.allerheiligenDayHoliday,
      bussUndBetTagHoliday: regionalHolidays.bussUndBetTagHoliday,
      augsburgerFriedensfestHoliday: regionalHolidays.augsburgerFriedensfestHoliday,
      christmasEveHoliday: regionalHolidays.christmasEveHoliday,
      newYearsEveHoliday: regionalHolidays.newYearsEveHoliday,
      easterSundayHoliday: regionalHolidays.easterSundayHoliday,
      pfingstSundayHoliday: regionalHolidays.pfingstSundayHoliday,
      pausesAreWorkingTime,
    };

    if (hasExistingQuotas === false) {
      payload.initialSurplusMinutes = surplusMinutes;
    }

    try {
      setIsSubmitting(true);
      const result = await timeTrackingApi.createTimeQuota(
        timeTrackingServiceModuleId,
        payload
      );
      if (!result.success()) {
        setErrorMessage(
          result.errorMessage ??
          t("employeesPage.workTimeQuota.errors.generic")
        );
        return;
      }

      setSuccessMessage(t("employeesPage.workTimeQuota.success"));
      onClose();
    } catch (error) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.generic"));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    workdays, regionalHolidays, createStartWeek,
    createEndWeek, createStartYear, createEndYear,
    pausesAreWorkingTime, initialSurplusHours, hasExistingQuotas
  ]);

  const handleUpdate = useCallback(async () => {
    setErrorMessage(null);
    setSuccessMessage(null);

    if (!canEdit) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.noPermission"));
      return;
    }

    if (!timeTrackingServiceModuleId) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.missingModule"));
      return;
    }

    if (editStartQuotaId === "") {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.missingWeek"));
      return;
    }

    const selectedIndex = sortedAllQuotas.findIndex(
      (quota) => quota.quotaId === editStartQuotaId
    );
    if (selectedIndex < 0) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.missingWeek"));
      return;
    }

    const quotasToUpdate = sortedAllQuotas.slice(selectedIndex);
    if (quotasToUpdate.length === 0) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.missingWeek"));
      return;
    }

    const payload = {
      minutesMonday: editWorkdays.monday * 60,
      minutesTuesday: editWorkdays.tuesday * 60,
      minutesWednesday: editWorkdays.wednesday * 60,
      minutesThursday: editWorkdays.thursday * 60,
      minutesFriday: editWorkdays.friday * 60,
      minutesSaturday: editWorkdays.saturday * 60,
      minutesSunday: editWorkdays.sunday * 60,
      holy3KingsDayHoliday: editRegionalHolidays.holy3KingsDay,
      internationalWomensHoliday: editRegionalHolidays.internationalWomensHoliday,
      fronleichnamHoliday: editRegionalHolidays.fronleichnamHoliday,
      mariaHimmelfahrtHoliday: editRegionalHolidays.mariaHimmelfahrtHoliday,
      worldChildrenDayHoliday: editRegionalHolidays.worldChildrenDayHoliday,
      reformationDayHoliday: editRegionalHolidays.reformationDayHoliday,
      allerheiligenDayHoliday: editRegionalHolidays.allerheiligenDayHoliday,
      bussUndBetTagHoliday: editRegionalHolidays.bussUndBetTagHoliday,
      christmasEveHoliday: editRegionalHolidays.christmasEveHoliday,
      newYearsEveHoliday: editRegionalHolidays.newYearsEveHoliday,
      easterSundayHoliday: editRegionalHolidays.easterSundayHoliday,
      pfingstSundayHoliday: editRegionalHolidays.pfingstSundayHoliday,
      augsburgerFriedensfestHoliday: editRegionalHolidays.augsburgerFriedensfestHoliday,
      pausesAreWorkingTime: editPausesAreWorkingTime,
    };

    console.log(payload);

    try {
      setIsUpdating(true);
      for (const quota of quotasToUpdate) {
        const result = await timeTrackingApi.updateTimeQuota(
          timeTrackingServiceModuleId,
          quota.quotaId,
          employee.id,
          payload
        );
        if (!result.success()) {
          setErrorMessage(
            result.errorMessage ?? t("employeesPage.workTimeQuota.errors.generic")
          );
          return;
        }
      }
      setSuccessMessage(t("employeesPage.workTimeQuota.updateSuccess"));
      onClose();
    } catch (error) {
      setErrorMessage(t("employeesPage.workTimeQuota.errors.generic"));
    } finally {
      setIsUpdating(false);
    }
  }, [editWorkdays, editRegionalHolidays, editPausesAreWorkingTime]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t("employeesPage.workTimeQuota.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {successMessage && <Alert severity="success">{successMessage}</Alert>}

          {hasExistingQuotas && firstQuota && lastQuota && (
            <Stack spacing={0.75}>
              <Typography variant="h6">
                {t("employeesPage.workTimeQuota.existingTitle")}
              </Typography>
              <Typography variant="subtitle1">
                {t("employeesPage.workTimeQuota.existingFirst", {
                  week: firstQuota.calendarWeek,
                  year: firstQuota.year,
                  hours: toHours(firstQuota.plannedWorkMinutes),
                })}
              </Typography>
              <Typography variant="subtitle1">
                {t("employeesPage.workTimeQuota.existingLast", {
                  week: lastQuota.calendarWeek,
                  year: lastQuota.year,
                  hours: toHours(lastQuota.plannedWorkMinutes),
                })}
              </Typography>
            </Stack>
          )}

          <Accordion
            expanded={isCreateExpanded}
            onChange={(_, expanded) => setIsCreateExpanded(expanded)}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1">
                {t("employeesPage.workTimeQuota.createTitle")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {hasExistingQuotas === false && (
                  <TextField
                    label={t("employeesPage.workTimeQuota.initialSurplusHours")}
                    type="number"
                    value={initialSurplusHours}
                    onChange={(event) => setInitialSurplusHours(event.target.value)}
                    slotProps={{ htmlInput: { min: 0, step: 0.25 } }}
                    fullWidth
                  />
                )}

                <Stack direction="row" spacing={2}>
                  <TextField
                    select
                    label={t("employeesPage.workTimeQuota.createFromYear")}
                    value={createStartYear}
                    onChange={(event) => {
                      const nextYear = Number(event.target.value);
                      if (Number.isFinite(nextYear)) {
                        setCreateStartYear(nextYear);
                        if (createEndYear < nextYear) {
                          setCreateEndYear(nextYear);
                        }
                      }
                    }}
                    fullWidth
                  >
                    {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((year) => (
                      <MenuItem key={`create-start-year-${year}`} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label={t("employeesPage.workTimeQuota.createFromWeek")}
                    value={createStartWeek}
                    onChange={(event) => {
                      const nextValue = Number(event.target.value);
                      const nextWeek = Number.isFinite(nextValue) ? nextValue : "";
                      setCreateStartWeek(nextWeek);
                      if (nextWeek !== "" && createEndWeek !== "" && nextWeek > createEndWeek) {
                        setCreateEndWeek(nextWeek);
                      }
                    }}
                    fullWidth
                  >
                    {startWeekOptions.map((week) => (
                      <MenuItem key={`create-start-${week}`} value={week}>
                        {t("employeesPage.workTimeQuota.weekLabel", {
                          week,
                          year: createStartYear,
                        })}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label={t("employeesPage.workTimeQuota.createUntilYear")}
                    value={createEndYear}
                    onChange={(event) => {
                      const nextYear = Number(event.target.value);
                      if (Number.isFinite(nextYear)) {
                        setCreateEndYear(nextYear);
                        if (createStartYear > nextYear) {
                          setCreateStartYear(nextYear);
                        }
                      }
                    }}
                    fullWidth
                  >
                    {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((year) => (
                      <MenuItem key={`create-end-year-${year}`} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label={t("employeesPage.workTimeQuota.createUntilWeek")}
                    value={createEndWeek}
                    onChange={(event) => {
                      const nextValue = Number(event.target.value);
                      setCreateEndWeek(Number.isFinite(nextValue) ? nextValue : "");
                    }}
                    fullWidth
                  >
                    {endWeekOptions
                      .filter((week) =>
                        createEndYear > createStartYear
                          ? true
                          : createStartWeek === "" || week >= createStartWeek
                      )
                      .map((week) => (
                        <MenuItem key={`create-end-${week}`} value={week}>
                          {t("employeesPage.workTimeQuota.weekLabel", {
                            week,
                            year: createEndYear,
                          })}
                        </MenuItem>
                      ))}
                  </TextField>
                </Stack>

                <WorkdaysEditor state={workdays} onUpdate={(v) => setWorkdays(v)} />

                <RegionalHolidays
                  value={regionalHolidays}
                  onUpdate={(v) => setRegionalHolidays(v)} />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={pausesAreWorkingTime}
                      onChange={(event) => setPausesAreWorkingTime(event.target.checked)}
                    />
                  }
                  label={t("employeesPage.workTimeQuota.pausesAreWorkingTime")}
                />
                <GeneralButton
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  disabled={!canEdit || isSubmitting}
                >
                  {t("buttons.save")}
                </GeneralButton>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {hasExistingQuotas && (
            <Accordion
              expanded={isEditExpanded}
              onChange={(_, expanded) => setIsEditExpanded(expanded)}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1">
                  {t("employeesPage.workTimeQuota.editTitle")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <TextField
                    select
                    label={t("employeesPage.workTimeQuota.editFromWeek")}
                    value={editStartQuotaId}
                    onChange={(event) => {
                      const nextValue = Number(event.target.value);
                      setEditStartQuotaId(Number.isFinite(nextValue) ? nextValue : "");
                    }}
                    fullWidth
                  >
                    {sortedAllQuotas.map((quota) => (
                      <MenuItem key={quota.quotaId} value={quota.quotaId}>
                        {t("employeesPage.workTimeQuota.weekLabel", {
                          week: quota.calendarWeek,
                          year: quota.year,
                        })}
                      </MenuItem>
                    ))}
                  </TextField>

                  <WorkdaysEditor state={editWorkdays} onUpdate={(v) => setEditWorkdays(v)} />

                  <RegionalHolidays
                    value={editRegionalHolidays}
                    onUpdate={(v) => setEditRegionalHolidays(v)} />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editPausesAreWorkingTime}
                        onChange={(event) =>
                          setEditPausesAreWorkingTime(event.target.checked)
                        }
                      />
                    }
                    label={t("employeesPage.workTimeQuota.pausesAreWorkingTime")}
                  />

                  <GeneralButton
                    onClick={handleUpdate}
                    loading={isUpdating}
                    disabled={!canEdit || isUpdating}
                  >
                    {t("employeesPage.workTimeQuota.updateButton")}
                  </GeneralButton>
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <GeneralButton appVariant="secondary" onClick={onClose}>
          {t("buttons.cancel")}
        </GeneralButton>
      </DialogActions>
    </Dialog>
  );
};

export default WorkTimeQuotaDialog;

interface WorkdaysEditorProps {
  state: WorkdaysState;
  onUpdate: (newState: WorkdaysState) => void;
}

function WorkdaysEditor(props: WorkdaysEditorProps) {
  const { t } = useTranslation();

  return <Stack spacing={1}>
    <Typography variant="subtitle1">
      {t("employeesPage.workTimeQuota.workdays")}
    </Typography>
    <Stack spacing={1} direction={"row"}>
      <TextField
        label={t("employeesPage.workTimeQuota.days.monday")}
        type="number"
        value={props.state.monday}
        onChange={(e) => props.onUpdate({
          ...props.state,
          monday: Number(e.target.value)
        })}
        slotProps={{ htmlInput: { min: 0, step: 0.25 } }}
      />
      <TextField
        label={t("employeesPage.workTimeQuota.days.tuesday")}
        type="number"
        value={props.state.tuesday}
        onChange={(e) => props.onUpdate({
          ...props.state,
          tuesday: Number(e.target.value)
        })}
        slotProps={{ htmlInput: { min: 0, step: 0.25 } }}
      />
      <TextField
        label={t("employeesPage.workTimeQuota.days.wednesday")}
        type="number"
        value={props.state.wednesday}
        onChange={(e) => props.onUpdate({
          ...props.state,
          wednesday: Number(e.target.value)
        })}
        slotProps={{ htmlInput: { min: 0, step: 0.25 } }}
      />
      <TextField
        label={t("employeesPage.workTimeQuota.days.thursday")}
        type="number"
        value={props.state.thursday}
        onChange={(e) => props.onUpdate({
          ...props.state,
          thursday: Number(e.target.value)
        })}
        slotProps={{ htmlInput: { min: 0, step: 0.25 } }}
      />
      <TextField
        label={t("employeesPage.workTimeQuota.days.friday")}
        type="number"
        value={props.state.friday}
        onChange={(e) => props.onUpdate({
          ...props.state,
          friday: Number(e.target.value)
        })}
        slotProps={{ htmlInput: { min: 0, step: 0.25 } }}
      />
      <TextField
        label={t("employeesPage.workTimeQuota.days.saturday")}
        type="number"
        value={props.state.saturday}
        onChange={(e) => props.onUpdate({
          ...props.state,
          saturday: Number(e.target.value)
        })}
        slotProps={{ htmlInput: { min: 0, step: 0.25 } }}
      />
      <TextField
        label={t("employeesPage.workTimeQuota.days.sunday")}
        type="number"
        value={props.state.sunday}
        onChange={(e) => props.onUpdate({
          ...props.state,
          sunday: Number(e.target.value)
        })}
        slotProps={{ htmlInput: { min: 0, step: 0.25 } }}
      />
    </Stack>
    <Typography variant="caption">
      {t("employeesPage.workTimeQuota.hoursPerDay")}
    </Typography>
  </Stack>
}
